import React, { useState } from 'react';
import { useAtom } from 'jotai';
import { View, Text, Pressable, Modal, ActivityIndicator } from '../../lib/react-native-compat';
import { selectedJobDataAtom, jobPhaseCodesAtom, jobCostDetailAtom } from '../../store/company';
import { fieldReportAtom, fieldReportDirtyAtom } from './state';
import { sectionDefs, jobWorkTypes } from './sectionDefs';
import PhotosStep from './PhotosStep';
import WorkPhaseChips, { useJobPhases, burnLabel } from './WorkPhaseChips';
import LaborMatrix from './LaborMatrix';
import EquipmentMatrix from './EquipmentMatrix';
import MaterialDeliveryCards from './MaterialDeliveryCards';
import IssueCards from './IssueCards';
import QuantityCards from './QuantityCards';
import { isPipelineJob } from './qtyModules';
import QualityChecklist from './QualityChecklist';
import SubDocsUpload, { DOC_KINDS } from './SubDocsUpload';
import AutoGrowTextarea from './AutoGrowTextarea';
import Lightbox from './Lightbox';

// Excel-style single-page entry view — mirrors the "Daily Report Mockups.xlsx"
// look (gridline tables, totals rows) while the numbered sections match the
// guided wizard's six steps: Report Setup (header info + safety + issue log),
// Work Performed, Quantities, Labor & Equipment, Material Deliveries, Quality.
// Photos and notes follow under Review & Submit, like the wizard's last step.
// Edits the same atoms as the wizard, so a draft can move freely between the
// two views.

const CELL_BORDER = '1px solid #CBD5E1';

const colWidth = (f) => {
    if (f.computed) return 95;
    switch (f.type) {
        case 'multiline': return 260;
        case 'number': return 95;
        case 'select': return 120;
        case 'bool': return 80;
        case 'time': return 95;
        case 'costCode': return 130;
        default: return 150;
    }
};

const cellInput = {
    width: '100%',
    border: 'none',
    outline: 'none',
    background: 'transparent',
    fontSize: 13,
    color: '#0F172A',
    padding: '7px 8px',
    boxSizing: 'border-box',
    fontFamily: 'inherit',
};

const thStyle = {
    border: CELL_BORDER,
    background: '#F1F5F9',
    padding: '6px 8px',
    fontSize: 11,
    fontWeight: 700,
    color: '#334155',
    textAlign: 'left',
    whiteSpace: 'nowrap',
};

const tdStyle = { border: CELL_BORDER, padding: 0, verticalAlign: 'top', background: '#fff' };

const SectionBar = ({ index, title }) => (
    <View style={{ backgroundColor: '#1E293B', paddingTop: 6, paddingBottom: 6, paddingLeft: 10, paddingRight: 10, marginTop: 18 }}>
        <Text style={{ fontSize: 12, fontWeight: '700', color: '#fff', letterSpacing: 0.8, textTransform: 'uppercase' }}>
            {index != null ? `${index}. ${title}` : title}
        </Text>
    </View>
);

const subHead = { fontSize: 13, fontWeight: '700', color: '#0F172A', marginTop: 14, marginBottom: 8 };

const SectionTable = ({ def, index, title, rows, setRows, readOnly, topContent, phaseCodes }) => {
    // Burn Tool schedule intervals — cost-code cells show the phase's
    // scheduled dates like the guided view's table and chips do.
    const { intervals } = useJobPhases();
    const updateRow = (i, name, value) => {
        const next = rows.slice();
        next[i] = { ...next[i], [name]: value };
        const fdef = def.fields.find((f) => f.name === name);
        if (def.autoAddOnCostCode && fdef?.type === 'costCode' && i === next.length - 1
            && phaseCodes?.[String(value).trim()]) next.push({});
        setRows(next);
    };
    const addRow = () => setRows([...rows, {}]);
    const delRow = (i) => setRows(rows.filter((_, idx) => idx !== i));

    // Map the section's totals entries onto columns by matching labels; any
    // leftover lands on the first free numeric column.
    const totalsByField = {};
    if (def.totals && rows.length > 0) {
        const unmatched = [];
        for (const t of def.totals(rows)) {
            const f = def.fields.find((x) => x.label === t.label);
            if (f) totalsByField[f.name] = t.value; else unmatched.push(t);
        }
        for (const t of unmatched) {
            const f = def.fields.find((x) => x.type === 'number' && !(x.name in totalsByField));
            if (f) totalsByField[f.name] = t.value;
        }
    }
    const hasTotals = Object.keys(totalsByField).length > 0;

    const renderCell = (f, row, i) => {
        const val = row[f.name] ?? '';
        if (f.computed) {
            const v = f.computed(row);
            return (
                <div style={{ ...cellInput, background: '#F8FAFC', color: '#475569', fontWeight: 600, textAlign: 'right' }}>
                    {v === '' || v == null ? '—' : v}
                </div>
            );
        }
        if (f.type === 'select' || f.type === 'bool') {
            const options = f.type === 'bool' ? ['Y', 'N'] : f.options || [];
            return (
                <select
                    value={val}
                    disabled={readOnly}
                    onChange={(e) => updateRow(i, f.name, e.target.value)}
                    style={{ ...cellInput, appearance: 'auto', cursor: readOnly ? 'default' : 'pointer' }}
                >
                    <option value="">—</option>
                    {options.map((o) => <option key={o} value={o}>{o}</option>)}
                </select>
            );
        }
        if (f.type === 'time') {
            return (
                <input
                    type="time"
                    value={val}
                    disabled={readOnly}
                    onChange={(e) => updateRow(i, f.name, e.target.value)}
                    style={cellInput}
                />
            );
        }
        if (f.type === 'costCode') {
            // Cost code + its phase description, like the guided view shows.
            const cc = val && phaseCodes?.[String(val).trim()];
            const bt = burnLabel(intervals, val);
            return (
                <div>
                    <input
                        type="text"
                        value={val}
                        disabled={readOnly}
                        list="fr-costcodes"
                        placeholder="Cost code…"
                        onChange={(e) => updateRow(i, f.name, e.target.value)}
                        style={cellInput}
                    />
                    {cc ? (
                        <div style={{ fontSize: 12, color: '#475569', padding: `0 8px ${bt ? 2 : 5}px` }}>{cc}</div>
                    ) : null}
                    {bt ? (
                        <div style={{ fontSize: 10, color: '#94A3B8', padding: '0 8px 5px', whiteSpace: 'nowrap' }}>{bt}</div>
                    ) : null}
                </div>
            );
        }
        if (f.type === 'multiline') {
            return (
                <AutoGrowTextarea
                    value={String(val)}
                    disabled={readOnly}
                    placeholder={f.placeholder || ''}
                    onChange={(e) => updateRow(i, f.name, e.target.value)}
                    style={{ ...cellInput, display: 'block' }}
                />
            );
        }
        return (
            <input
                type="text"
                inputMode={f.type === 'number' ? 'decimal' : undefined}
                value={String(val)}
                disabled={readOnly}
                list={f.type === 'vendor' ? 'fr-vendors' : f.type === 'po' ? 'fr-pos' : undefined}
                placeholder={f.placeholder || ''}
                onChange={(e) => updateRow(i, f.name, e.target.value)}
                style={{ ...cellInput, textAlign: f.type === 'number' ? 'right' : 'left' }}
            />
        );
    };

    return (
        <View>
            {index != null && <SectionBar index={index} title={title || def.title} />}
            {topContent ? <View style={{ marginTop: 8 }}>{topContent}</View> : null}
            <div style={{ overflowX: 'auto' }}>
                <table style={{ borderCollapse: 'collapse', width: '100%' }}>
                    <thead>
                        <tr>
                            {def.fields.map((f) => (
                                <th key={f.name} style={{ ...thStyle, minWidth: colWidth(f) }}>{f.label}</th>
                            ))}
                            {!readOnly && <th style={{ ...thStyle, minWidth: 30, width: 30 }} />}
                        </tr>
                    </thead>
                    <tbody>
                        {rows.length === 0 && (
                            <tr>
                                <td colSpan={def.fields.length + (readOnly ? 0 : 1)} style={{ ...tdStyle, background: '#F8FAFC' }}>
                                    <div style={{ ...cellInput, color: '#94A3B8', fontStyle: 'italic' }}>
                                        {readOnly ? 'No entries.' : `No ${def.rowNoun} entries — add a row below.`}
                                    </div>
                                </td>
                            </tr>
                        )}
                        {rows.map((row, i) => (
                            <tr key={i}>
                                {def.fields.map((f) => (
                                    <td key={f.name} style={tdStyle}>{renderCell(f, row, i)}</td>
                                ))}
                                {!readOnly && (
                                    <td style={{ ...tdStyle, textAlign: 'center', verticalAlign: 'middle' }}>
                                        <button
                                            onClick={() => delRow(i)}
                                            title="Remove row"
                                            style={{ border: 'none', background: 'transparent', color: '#DC2626', cursor: 'pointer', fontSize: 14, lineHeight: 1, padding: '6px 8px' }}
                                        >×</button>
                                    </td>
                                )}
                            </tr>
                        ))}
                        {hasTotals && (
                            <tr>
                                {def.fields.map((f, ci) => (
                                    <td key={f.name} style={{ ...tdStyle, background: '#F1F5F9' }}>
                                        <div style={{ ...cellInput, fontWeight: 700, color: '#0F172A', textAlign: ci === 0 ? 'left' : 'right' }}>
                                            {f.name in totalsByField ? totalsByField[f.name] : (ci === 0 ? 'Totals' : '')}
                                        </div>
                                    </td>
                                ))}
                                {!readOnly && <td style={{ ...tdStyle, background: '#F1F5F9' }} />}
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
            {!readOnly && (
                <Pressable onPress={addRow} style={{ alignSelf: 'flex-start', marginTop: 4, cursor: 'pointer', paddingTop: 4, paddingBottom: 4, paddingRight: 8 }}>
                    <Text style={{ fontSize: 12, fontWeight: '600', color: '#1D4ED8' }}>{`+ Add ${def.rowNoun}`}</Text>
                </Pressable>
            )}
        </View>
    );
};

// Header info moved into the dark title bar — three columns of label / value
// pairs instead of a separate grid table under Report Setup, so the top of the
// sheet stays compact. Inputs are transparent on the navy so the bar still
// reads as a header; colorScheme dark keeps the native date picker legible.
const hdrLabel = {
    fontSize: 10,
    fontWeight: 700,
    color: '#94A3B8',
    textTransform: 'uppercase',
    letterSpacing: 0.6,
    width: 92,
    flexShrink: 0,
};

const hdrInput = {
    flex: 1,
    minWidth: 0,
    border: 'none',
    borderBottom: '1px solid #334155',
    outline: 'none',
    background: 'transparent',
    fontSize: 13,
    color: '#F1F5F9',
    padding: '3px 2px',
    fontFamily: 'inherit',
    colorScheme: 'dark',
};

const HdrRow = ({ label, children }) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, minHeight: 26 }}>
        <span style={hdrLabel}>{label}</span>
        {children}
    </div>
);

const SheetView = ({ readOnly, onSaveDraft, onSubmit, saving }) => {
    const [report, setReport] = useAtom(fieldReportAtom);
    const [, setDirty] = useAtom(fieldReportDirtyAtom);
    const [jobData] = useAtom(selectedJobDataAtom);
    const [phaseCodes] = useAtom(jobPhaseCodesAtom);
    const [jobCostDetail] = useAtom(jobCostDetailAtom);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [docLightbox, setDocLightbox] = useState(null);

    if (!report) return null;

    // The job's project type (Form_Job_Master Work_Type) for the title bar.
    const workTypes = jobWorkTypes(jobData, jobCostDetail, phaseCodes);

    const set = (name) => (value) => {
        setReport({ ...report, [name]: value });
        setDirty(true);
    };
    const setRows = (key) => (next) => {
        setReport({ ...report, sections: { ...report.sections, [key]: next } });
        setDirty(true);
    };

    const jobName = jobData?.BU_Description || jobData?.Job_Description || '';

    // Every non-photo upload (delivery tickets from Material Deliveries, sub
    // time tickets / progress reports from Labor & Equipment) gathers under
    // Review & Submit so the last look before submit shows all attachments,
    // not just photos. View-only — each doc is managed from its own section.
    const docKinds = {
        deliveryTicket: { tag: 'DT', label: 'Delivery Ticket' },
        ...Object.fromEntries(DOC_KINDS.map((k) => [k.key, k])),
    };
    const reviewDocs = (report.photos || []).filter((p) => p.docType && p.docType !== 'photo');
    const docMetaOf = (id) => (report.sections?.photoMeta || []).find((m) => m.photoId === id) || {};
    // Which delivery a ticket belongs to — supplier + PO off its materials row.
    const deliveryOf = (id) => {
        const row = (report.sections?.materials || []).find((r) => (r.ticketPhotoIds || []).includes(id));
        return row ? [row.supplier, row.poNumber ? `PO ${row.poNumber}` : null].filter(Boolean).join(' · ') : '';
    };

    // Numbered sections mirror the wizard's six steps. Safety and the Issue
    // Log render inside section 1 (Report Setup), like the wizard's Setup tab,
    // so they drop out of the numbered list. Labor and Equipment share one
    // numbered section; the equipment def only contributes its totals footer.
    const stepTitles = {
        work: 'Work Performed',
        quantities: 'Quantities',
        labor: 'Labor & Equipment',
        materials: 'Material Deliveries',
        quality: 'Quality',
    };
    const visibleDefs = sectionDefs.filter((def) =>
        !def.hidden && !def.matrixEquip && def.key !== 'safety' && def.key !== 'delays');
    const equipDef = sectionDefs.find((def) => def.matrixEquip);
    const equipTotals = equipDef?.totals ? equipDef.totals(report.sections?.equipment || []) : [];
    const safetyDef = sectionDefs.find((def) => def.key === 'safety');

    // Safety stores as section rows (same storage the Setup tab uses); a
    // change to the first row's Safety Events mirrors to the report header
    // column, matching the wizard.
    const safetyRows = report.sections?.safety || [];
    const setSafetyRows = (next) => {
        const mirror = (next[0]?.incidents ?? '') !== (safetyRows[0]?.incidents ?? '')
            ? { safetyIncidents: next[0]?.incidents ?? '' } : null;
        setReport({ ...report, ...mirror, sections: { ...report.sections, safety: next } });
        setDirty(true);
    };

    const infoInput = (name, props = {}) => (
        <input
            type="text"
            className="fr-hdr-input"
            value={String(report[name] ?? '')}
            disabled={readOnly}
            onChange={(e) => set(name)(e.target.value)}
            style={hdrInput}
            {...props}
        />
    );
    const infoStatic = (text) => (
        <span style={{ flex: 1, minWidth: 0, fontSize: 13, fontWeight: 600, color: '#F1F5F9' }}>{text}</span>
    );

    return (
        <View style={{ paddingBottom: 24 }}>
            {/* Title bar — identity on the left, the report's header fields in
                two columns beside it (they used to be a separate grid table
                under Report Setup). */}
            <style>{'.fr-hdr-input::placeholder { color: #64748B; opacity: 1; }'}</style>
            <View style={{ backgroundColor: '#0F172A', paddingTop: 12, paddingBottom: 12, paddingLeft: 16, paddingRight: 16 }}>
                <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
                    columnGap: 28,
                    rowGap: 12,
                    alignItems: 'start',
                }}>
                    {/* A View, not a div — compat Text renders an inline span,
                        so these only stack as rows inside a flex column. */}
                    <View>
                        <Text style={{ fontSize: 16, fontWeight: '700', color: '#fff' }}>
                            {`${report.buId}${jobName ? ' — ' + jobName : ''}`}
                        </Text>
                        <Text style={{ fontSize: 11, fontWeight: '700', color: '#94A3B8', letterSpacing: 1.2, marginTop: 4 }}>
                            DAILY FIELD REPORT
                        </Text>
                        {workTypes.length > 0 && (
                            <Text style={{ fontSize: 10, fontWeight: '600', color: '#94A3B8', marginTop: 3, letterSpacing: 0.5 }}>
                                {workTypes.join(' · ')}
                            </Text>
                        )}
                    </View>
                    <div>
                        <HdrRow label="Report #">
                            {infoStatic(`${report.reportNumber ? `DFR-${report.reportNumber}` : 'Assigned on save'} · ${report.status || 'Draft'}`)}
                        </HdrRow>
                        <HdrRow label="Date">
                            <input
                                type="date"
                                value={report.reportDate || ''}
                                disabled={readOnly}
                                onChange={(e) => set('reportDate')(e.target.value)}
                                style={hdrInput}
                            />
                        </HdrRow>
                        <HdrRow label="Superintendent">{infoInput('superintendent', { placeholder: 'Name' })}</HdrRow>
                        <HdrRow label="Prepared By">{infoInput('preparedBy')}</HdrRow>
                    </div>
                    <div>
                        <HdrRow label="Weather AM">{infoInput('weatherAM', { placeholder: 'Clear, 68°F' })}</HdrRow>
                        <HdrRow label="Weather PM">{infoInput('weatherPM', { placeholder: 'Partly cloudy, 84°F' })}</HdrRow>
                        <HdrRow label="Wind">{infoInput('wind', { placeholder: 'SW 10–15 mph' })}</HdrRow>
                        <HdrRow label="Ground Cond.">{infoInput('groundConditions', { placeholder: 'Dry / Firm' })}</HdrRow>
                    </div>
                </div>
            </View>

            {/* 1. Report Setup — safety + issue log, the same contents as the
                wizard's Setup tab minus the header fields now in the bar. */}
            <SectionBar index={1} title="Report Setup" />
            <Text style={subHead}>Safety</Text>
            <SectionTable
                def={safetyDef}
                rows={safetyRows}
                setRows={setSafetyRows}
                readOnly={readOnly}
                phaseCodes={phaseCodes}
            />
            <Text style={subHead}>Issues / Delays</Text>
            <IssueCards readOnly={readOnly} hideBlank />

            {/* Sections 2–6, in wizard-step order. The specialized editors
                from the guided view render here too (labor + sub docs,
                equipment picker, delivery cards, quality checklist) so the two
                views match; the remaining sections stay Excel-style tables. */}
            {visibleDefs.map((def, i) => (
                def.matrix ? (
                    <View key={def.key}>
                        <SectionBar index={i + 2} title="Labor & Equipment" />
                        <Text style={{ fontSize: 13, fontWeight: '700', color: '#0F172A', marginTop: 10, marginBottom: 8 }}>Labor & Crew On Site</Text>
                        <LaborMatrix readOnly={readOnly} />
                        <SubDocsUpload readOnly={readOnly} totals={def.totals ? def.totals(report.sections?.labor || []) : []} />
                        <Text style={{ fontSize: 13, fontWeight: '700', color: '#0F172A', marginTop: 20, marginBottom: 8 }}>Equipment Used</Text>
                        <EquipmentMatrix readOnly={readOnly} />
                        {equipTotals.length > 0 && (report.sections?.equipment || []).length > 0 && (
                            <View style={{ borderRadius: 10, backgroundColor: '#F1F5F9', padding: 12, flexDirection: 'row', flexWrap: 'wrap', columnGap: 18, rowGap: 8, marginTop: 12 }}>
                                {equipTotals.map((t) => (
                                    <View key={t.label}>
                                        <Text style={{ fontSize: 10, fontWeight: '600', color: '#64748B', textTransform: 'uppercase', letterSpacing: 0.5 }}>{t.label}</Text>
                                        <Text style={{ fontSize: 16, fontWeight: '700', color: '#0F172A', fontVariantNumeric: 'tabular-nums' }}>{t.value}</Text>
                                    </View>
                                ))}
                            </View>
                        )}
                    </View>
                ) : def.deliveryCards ? (
                    <View key={def.key}>
                        <SectionBar index={i + 2} title={stepTitles[def.key] || def.title} />
                        <View style={{ paddingTop: 8 }}>
                            <MaterialDeliveryCards readOnly={readOnly} layout="table" />
                        </View>
                    </View>
                ) : def.key === 'quantities' && isPipelineJob(jobData, workTypes) ? (
                    // Pipeline jobs get the tracker-module cards here too, so
                    // Sheet view and the wizard capture the same quantities.
                    <View key={def.key}>
                        <SectionBar index={i + 2} title={stepTitles[def.key] || def.title} />
                        <View style={{ paddingTop: 10 }}>
                            <QuantityCards readOnly={readOnly} />
                        </View>
                    </View>
                ) : def.key === 'quality' ? (
                    <View key={def.key}>
                        <SectionBar index={i + 2} title={stepTitles[def.key] || def.title} />
                        <View style={{ paddingTop: 10 }}>
                            <QualityChecklist readOnly={readOnly} />
                        </View>
                    </View>
                ) : (
                <SectionTable
                    key={def.key}
                    def={def}
                    index={i + 2}
                    title={stepTitles[def.key]}
                    rows={report.sections?.[def.key] || []}
                    setRows={setRows(def.key)}
                    readOnly={readOnly}
                    phaseCodes={phaseCodes}
                    topContent={def.key === 'work' ? <WorkPhaseChips readOnly={readOnly} /> : null}
                />
                )
            ))}

            {/* Review & Submit — photos + catch-all notes, same as the
                wizard's final step (unnumbered so the numbered sections stay
                the wizard's six). */}
            <SectionBar title="Review & Submit" />
            <Text style={subHead}>Photos & Markups</Text>
            <PhotosStep readOnly={readOnly} />
            {reviewDocs.length > 0 && (
                <>
                    <Text style={subHead}>Tickets & Documents</Text>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                        {reviewDocs.map((doc) => {
                            const meta = docMetaOf(doc.photoId);
                            const kind = docKinds[meta.docType || doc.docType] || { tag: 'DOC', label: 'Document' };
                            const isPdf = String(doc.contentType || '').includes('pdf') || /\.pdf$/i.test(doc.location || '');
                            const sub = doc.docType === 'deliveryTicket'
                                ? deliveryOf(doc.photoId)
                                : (meta.caption ?? doc.caption) || '';
                            const thumb = doc.sasUrl && !isPdf ? (
                                <img
                                    src={doc.sasUrl}
                                    alt=""
                                    onClick={() => setDocLightbox({ src: doc.sasUrl, title: doc.location || kind.label })}
                                    style={{ width: 56, height: 56, objectFit: 'cover', borderRadius: 6, display: 'block', border: '1px solid #E2E8F0', cursor: 'pointer', flexShrink: 0 }}
                                />
                            ) : (
                                <a href={doc.sasUrl || undefined} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none', flexShrink: 0 }}>
                                    <div style={{ width: 56, height: 56, borderRadius: 6, background: '#F1F5F9', border: '1px solid #E2E8F0', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 700, color: '#64748B' }}>
                                        {isPdf ? 'PDF' : 'DOC'}
                                    </div>
                                </a>
                            );
                            return (
                                <div key={doc.photoId} style={{ display: 'flex', alignItems: 'center', gap: 10, border: '1px solid #E2E8F0', borderRadius: 10, background: '#fff', padding: 8, width: 250, boxSizing: 'border-box' }}>
                                    {thumb}
                                    <div style={{ minWidth: 0, flex: 1 }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                            <span style={{ border: '1px solid #94A3B8', borderRadius: 4, padding: '1px 5px', fontSize: 10, fontWeight: 700, color: '#475569', letterSpacing: 0.5, flexShrink: 0 }}>{kind.tag}</span>
                                            <span style={{ fontSize: 11, fontWeight: 600, color: '#475569', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{kind.label}</span>
                                        </div>
                                        <div style={{ fontSize: 11, color: '#64748B', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginTop: 3 }}>
                                            {doc.location || 'Document'}
                                        </div>
                                        {sub ? (
                                            <div style={{ fontSize: 11, color: '#94A3B8', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginTop: 2 }}>{sub}</div>
                                        ) : null}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </>
            )}
            <Text style={subHead}>Additional Comments / Notes</Text>
            <AutoGrowTextarea
                value={String(report.notes ?? '')}
                disabled={readOnly}
                minHeight={72}
                placeholder="Enter additional comments, observations, or notes…"
                onChange={(e) => set('notes')(e.target.value)}
                style={{ ...cellInput, border: CELL_BORDER, background: '#fff', display: 'block' }}
            />

            {/* Actions */}
            {!readOnly && (
                <View style={{ flexDirection: 'row', justifyContent: 'flex-end', columnGap: 10, marginTop: 16 }}>
                    <Pressable
                        onPress={onSaveDraft}
                        disabled={saving}
                        style={{ minHeight: 44, paddingLeft: 18, paddingRight: 18, borderRadius: 8, backgroundColor: '#EFF6FF', border: '1px solid #BFDBFE', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}
                    >
                        <Text style={{ fontSize: 14, fontWeight: '700', color: '#1D4ED8' }}>{saving ? 'Saving…' : 'Save Draft'}</Text>
                    </Pressable>
                    <Pressable
                        onPress={() => !saving && setConfirmOpen(true)}
                        disabled={saving}
                        style={{ minHeight: 44, paddingLeft: 18, paddingRight: 18, borderRadius: 8, backgroundColor: '#1D4ED8', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}
                    >
                        <Text style={{ fontSize: 14, fontWeight: '700', color: '#fff' }}>Submit Report</Text>
                    </Pressable>
                </View>
            )}
            {readOnly && (
                <View style={{ marginTop: 16, alignItems: 'flex-end' }}>
                    <Text style={{ fontSize: 13, fontWeight: '600', color: '#065F46' }}>Submitted — read-only</Text>
                </View>
            )}

            <Modal visible={confirmOpen} transparent animationType="fade" onRequestClose={() => setConfirmOpen(false)}>
                <View style={{ flex: 1, backgroundColor: 'rgba(15,23,42,0.5)', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
                    <View style={{ borderRadius: 14, backgroundColor: '#fff', padding: 20, width: '100%', maxWidth: 420 }}>
                        <Text style={{ fontSize: 16, fontWeight: '700', color: '#0F172A', marginBottom: 8 }}>Submit this report?</Text>
                        <Text style={{ fontSize: 13, color: '#475569', marginBottom: 16 }}>
                            Submitting locks the report — it becomes read-only for the field. Make sure quantities, hours and photos are complete.
                        </Text>
                        <View style={{ flexDirection: 'row', columnGap: 10 }}>
                            <Pressable
                                onPress={() => setConfirmOpen(false)}
                                style={{ flex: 1, minHeight: 46, borderRadius: 10, border: '1px solid #CBD5E1', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}
                            >
                                <Text style={{ fontSize: 14, fontWeight: '600', color: '#334155' }}>Cancel</Text>
                            </Pressable>
                            <Pressable
                                onPress={() => { setConfirmOpen(false); onSubmit(); }}
                                style={{ flex: 1, minHeight: 46, borderRadius: 10, alignItems: 'center', justifyContent: 'center', backgroundColor: '#16A34A', cursor: 'pointer', flexDirection: 'row' }}
                            >
                                {saving ? <ActivityIndicator size="small" color="#fff" /> : null}
                                <Text style={{ fontSize: 14, fontWeight: '700', color: '#fff', marginLeft: saving ? 6 : 0 }}>Submit</Text>
                            </Pressable>
                        </View>
                    </View>
                </View>
            </Modal>

            {docLightbox && <Lightbox src={docLightbox.src} title={docLightbox.title} onClose={() => setDocLightbox(null)} />}
        </View>
    );
};

export default SheetView;
